import { Box, Button, CircularProgress } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useState } from 'react';
import {
  Form,
  Link,
  PasswordInput,
  TextInput,
  required,
  useLogin,
  useNotify,
  useTranslate,
} from 'react-admin';
import { useLocation } from 'react-router-dom';
import Barras from '../../assets/img/barras.png';
import Logo from '../../assets/img/logo-header.svg';
import { useUser } from '../../providers/userProvider';

const useStyles = makeStyles(theme => ({
  main: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    backgroundColor: '#3F49DB',
  },
  side: {
    background: `url(${Barras})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    flex: 1,
    display: 'flex',
    alignItems: 'flex-end',
    flexDirection: 'row',
    gap: '8px',
    width: '100%',
    height: '100%',
    [theme.breakpoints.up('lg')]: {
      backgroundSize: '108% 110%',
    },
  },
  copyright: {
    color: '#FFFFFF',
    fontSize: '14px',
    lineHeight: '19px',
    fontWeight: '400',
    letterSpacing: '0.5%',
    marginBottom: '10px',
    paddingLeft: '10px',
    '& a': {
      color: '#FFFFFF',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  formContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    height: '100%',
    padding: '32px 16px',
    background: '#f3f3f3',
    marginLeft: 'auto',
    [theme.breakpoints.up('md')]: {
      flex: 0.8,
      borderRadius: '50px 0px 0px 50px',
    },
  },
  welcome: {
    color: '#171a1f',
    textAlign: 'center',
    fontSize: '32px',
    lineHeight: '37px',
    fontWeight: '700',
    margin: '8px 0',
  },
  subtitle: {
    color: '#171a1f',
    textAlign: 'center',
    fontSize: '16px',
    lineHeight: '21px',
    fontWeight: '400',
    margin: 0,
    marginBottom: '24px',
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '380px',
  },
  forget: {
    width: '100%',
    textAlign: 'right',
    marginBottom: '24px',
    position: 'relative',
    top: '-2px',
    '& a': {
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  'google-login': {
    all: 'unset',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    padding: '8px 20px 8px 20px',
    width: '100%',
    justifyContent: 'center',
    borderRadius: '500px',
    transition: 'background 200ms ease-in-out',
    background: '#fff',
    boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.1)',
    fontSize: '14px',
    lineHeight: '19px',
    fontWeight: '600',
    letterSpacing: '0.5%',
    '&:hover ': {
      boxShadow:
        '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
    },
    '& svg': {
      width: '24px',
      height: '24px',
    },
    '&:not(disabled)': {
      background: '#e5e5e5!important',
      boxShadow: 'none!important',
      cursor: 'default',
    },
  },
  'google-divider': {
    margin: '16px 0',
    fontSize: '16px',
    lineHeight: '23px',
    fontWeight: '600',
  },
}));

const Login = () => {
  const [loading, setLoading] = useState(false);
  const translate = useTranslate();
  const notify = useNotify();
  const login = useLogin();
  const location = useLocation();
  const { asyncUserInfo } = useUser();

  const handleSubmit = auth => {
    setLoading(true);
    login(auth, location.state ? location.state.nextPathname : '/')
      .catch(error => {
        notify(
          typeof error === 'string'
            ? error
            : typeof error === 'undefined' || !error.message
              ? 'ra.auth.sign_in_error'
              : error.message,
          {
            type: 'error',
            messageArgs: {
              _:
                typeof error === 'string'
                  ? error
                  : error && error.message
                    ? error.message
                    : undefined,
            },
          }
        );
      })

      .finally(() => {
        asyncUserInfo();
        setLoading(false);
      });
  };

  const classes = useStyles();

  return (
    <Form onSubmit={handleSubmit} className={classes.main} noValidate>
      <Box
        className={classes.side}
        sx={{
          display: {
            xs: 'none',
            md: 'flex',
          },
        }}
      >
        <Box className={classes.copyright}>
          <span>Copyright © {new Date().getFullYear()} | </span>
          <Link to="/" className={classes.link}>
            <span>Politica de privacidad</span>
          </Link>
        </Box>
      </Box>

      <Box className={`${classes.formContainer}`}>
        <img src={Logo} width={160} style={{ marginBottom: '24px' }} alt="" />
        <h1 className={classes.welcome}>¡Bienvenido!</h1>
        <p className={classes.subtitle}>Iniciá sesión para empezar</p>
        <Box className={`${classes.card}`}>
          <TextInput
            validate={required()}
            name="username"
            source="username"
            label={translate('ra.auth.email')}
            variant={'filled'}
            fullWidth
          />
          <PasswordInput
            validate={required()}
            name="password"
            source="password"
            variant={'filled'}
            label={translate('ra.auth.password')}
            fullWidth
          />
          <Box className={classes.forget}>
            <Link color="primary" to="/recuperar-password">
              {translate('ra.auth.forgetPassword')}
            </Link>
          </Box>
          <Button
            variant="contained"
            type="submit"
            color="primary"
            disabled={loading}
            fullWidth
            sx={{ height: '40px' }}
          >
            {loading && (
              <CircularProgress
                size={18}
                thickness={5}
                sx={{ color: 'white' }}
              />
            )}
            {!loading && translate('ra.auth.sign_in')}
          </Button>
          {/* <>
            <span className={classes['google-divider']}>O</span>
            <button
              className={`${classes['google-login']}`}
              onClick={() => handleGoogleLogin()}
              type="button"
              disabled
            >
              {googleIcon}
              <span>Iniciar sesión con Google</span>
            </button>
          </> */}
        </Box>
      </Box>
    </Form>
  );
};

const CustomLogin = props => {
  return <Login {...props} />;
};

export default CustomLogin;
